import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import axios from 'axios';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Box,
  Link
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
//import './LearningSum.css';
import { formatDate } from '../../utils/dateFormatter';
const LearningSum = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState('current');
  const [teamSize, setTeamSize] = useState(0);
  const [teamSummary, setTeamSummary] = useState({
    overview: { totalEnrollments: 0, totalViews: 0, totalCompletions: 0 },
    members: []
  });
  const [learningsData, setLearningsData] = useState([]);
  const [currentView, setCurrentView] = useState('main-team-table');
  const [courseData, setCourseData] = useState([]);
  const [dataTypeLabel, setDataTypeLabel] = useState('Items');
  const [memberData, setMemberData] = useState({
    enrollments: [],
    views: [],
    completions: []
  });
  const [selectedMember, setSelectedMember] = useState({ id: null, name: '' });
  const [learningPeopleData, setLearningPeopleData] = useState([]);
  const [selectedLearning, setSelectedLearning] = useState({
    id: null,
    name: '',
    type: '',
    dataType: ''
  });

  // Check if user is a manager
  useEffect(() => {
    if (user && user.role_name?.toLowerCase() !== 'manager') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Get date range based on selection
  const getDateRange = (selectedRange) => {
    const today = new Date();
    let startDate, endDate;
    
    switch(selectedRange) {
      case 'current':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
        
      case 'last3months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
        
      case 'lastyear':
        startDate = new Date(today.getFullYear() - 1, today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
    }
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    //return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return formatDate(date.toISOString());
  };

  // Fetch team data
  const fetchTeamData = async (startDate, endDate) => {
    try {
      const response = await axios.get(`/api/dashboard/team-data?startDate=${startDate}&endDate=${endDate}`);
      setTeamSummary(response.data);
      setTeamSize(response.data.overview.totalTeamMembers || 0);
    } catch (error) {
      console.error('Error fetching team data:', error);
    }
  };

  // Fetch course data
  const fetchCourseData = async (dataType) => {
    const { startDate, endDate } = getDateRange(dateRange);
    
    try {
      const response = await axios.get(`/api/dashboard/course-data?startDate=${startDate}&endDate=${endDate}&dataType=${dataType}`);
      setCourseData(response.data);
      setDataTypeLabel(dataType.charAt(0).toUpperCase() + dataType.slice(1));
      setCurrentView('course-data-table');
    } catch (error) {
      console.error(`Error fetching ${dataType} data:`, error);
    }
  };

  // Fetch member data
  const fetchMemberData = async (dataType, memberId, memberName) => {
    const { startDate, endDate } = getDateRange(dateRange);
    const endpoint = `/api/dashboard/member-${dataType}`;
    
    try {
      const response = await axios.get(`${endpoint}?memberId=${memberId}&startDate=${startDate}&endDate=${endDate}`);
      setMemberData({
        ...memberData,
        [dataType]: response.data
      });
      setSelectedMember({ id: memberId, name: memberName });
      setCurrentView(`member-${dataType}-table`);
    } catch (error) {
      console.error(`Error fetching member ${dataType}:`, error);
    }
  };

  // Fetch learnings data
  const fetchLearningsData = async (startDate, endDate) => {
    try {
      const response = await axios.get(`/api/dashboard/learnings-data?startDate=${startDate}&endDate=${endDate}`);
      setLearningsData(response.data);
    } catch (error) {
      console.error('Error fetching learnings data:', error);
    }
  };

  // Fetch learning people data
  const fetchLearningPeopleData = async (dataType, learningId, learningType, learningName) => {
    const { startDate, endDate } = getDateRange(dateRange);
  
    try {
      const response = await axios.get(
        `/api/dashboard/learning-people?learningId=${learningId}&learningType=${learningType}&dataType=${dataType}&startDate=${startDate}&endDate=${endDate}`
      );
      setLearningPeopleData(response.data);
      setSelectedLearning({
        id: learningId,
        name: learningName,
        type: learningType,
        dataType
      });
      setCurrentView('learning-people-table');
    } catch (error) {
      console.error(`Error fetching learning ${dataType} people:`, error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const { startDate, endDate } = getDateRange(dateRange);
    fetchTeamData(startDate, endDate);
    if (activeTab === 1) {
      fetchLearningsData(startDate, endDate);
    }
  }, [dateRange, activeTab]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setCurrentView('main-team-table');
      const { startDate, endDate } = getDateRange(dateRange);
      fetchTeamData(startDate, endDate);
    } else {
      setCurrentView('learnings-main-table');
      const { startDate, endDate } = getDateRange(dateRange);
      fetchLearningsData(startDate, endDate);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (event) => {
    setDateRange(event.target.value);
  };

  return (
    <Container maxWidth="xl" className="dashboard-container">
      <Grid container spacing={3} className="mb-4">
        <Grid item xs={12} md={6}>
          <Typography variant="body1" color="textSecondary">
            Comprehensive view of your team's learning activities
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
          <FormControl variant="outlined" size="small" className="date-filter">
            <Select
              value={dateRange}
              onChange={handleDateRangeChange}
              displayEmpty
            >
              <MenuItem value="current">Current Month to Today</MenuItem>
              <MenuItem value="last3months">Last 3 Months</MenuItem>
              <MenuItem value="lastyear">Last Year</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Team Size Info */}
      <Grid container spacing={3} className="mb-4">
        <Grid item xs={12} sx={{ mb: 1 }}>
              <Typography variant="h5" component="h2">
                Team Size: {teamSize}
              </Typography>
        </Grid>
      </Grid>

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Team" id="team-tab" />
          <Tab label="Learnings View" id="learnings-tab" />
        </Tabs>
      </Paper>

      {/* Team Tab Content */}
      {activeTab === 0 && (
        <div>
          {currentView === 'main-team-table' && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Team & Members</TableCell>
                    <TableCell>Enrollment</TableCell>
                    <TableCell>In Progress</TableCell>
                    <TableCell>Completions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Link 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentView('team-members-table');
                        }}
                      >
                        Manager's Team ({teamSize})
                      </Link>
                    </TableCell>
                    <TableCell>
                    <Link 
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          fetchCourseData('enrollments');
                        }}
                      >
                        {teamSummary.overview.totalEnrollments}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link 
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          fetchCourseData('views');
                        }}
                      >
                        {teamSummary.overview.totalViews}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link 
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          fetchCourseData('completions');
                        }}
                      >
                        {teamSummary.overview.totalCompletions}
                      </Link>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {currentView === 'team-members-table' && (
            <div>
              <Box sx={{ mb: 2 }}>
                <Link 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentView('main-team-table');
                  }}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <ArrowBack fontSize="small" sx={{ mr: 1 }} />
                  Back to Team Summary
                </Link>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Team & Members</TableCell>
                      <TableCell>Enrollment</TableCell>
                      <TableCell>In Progress</TableCell>
                      <TableCell>Completions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamSummary.members && teamSummary.members.length > 0 ? (
                      teamSummary.members.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>{member.name}</TableCell>
                          <TableCell>
                            <Link 
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                fetchMemberData('enrollments', member.id, member.name);
                              }}
                            >
                              {member.enrollments}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Link 
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                fetchMemberData('views', member.id, member.name);
                              }}
                            >
                              {member.views}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Link 
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                fetchMemberData('completions', member.id, member.name);
                              }}
                            >
                              {member.completions}
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No team members found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}

          {currentView === 'course-data-table' && (
            <div>
              <Box sx={{ mb: 2 }}>
                <Link 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentView('main-team-table');
                  }}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <ArrowBack fontSize="small" sx={{ mr: 1 }} />
                  Back to Team Summary
                </Link>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Learnings ({courseData.length})</TableCell>
                      <TableCell>{dataTypeLabel}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {courseData && courseData.length > 0 ? (
                      courseData.map((course, index) => (
                        <TableRow key={index}>
                          <TableCell>{course.name}</TableCell>
                          <TableCell>{course[dataTypeLabel.toLowerCase()]}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">No {dataTypeLabel.toLowerCase()} data found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}

          {['member-enrollments-table', 'member-views-table', 'member-completions-table'].includes(currentView) && (
            <div>
              <Box sx={{ mb: 2 }}>
                <Link 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentView('team-members-table');
                  }}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <ArrowBack fontSize="small" sx={{ mr: 1 }} />
                  Back to Team Members
                </Link>
              </Box>
              <Typography variant="h6" gutterBottom>
                {currentView === 'member-enrollments-table' ? 'Enrollments' : 
                 currentView === 'member-views-table' ? 'In Progress' : 'Completions'} for {selectedMember.name}
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Learnings ({
                        currentView === 'member-enrollments-table' ? memberData.enrollments.length : 
                        currentView === 'member-views-table' ? memberData.views.length : 
                        memberData.completions.length
                      })</TableCell>
                      <TableCell>Enrollment Date</TableCell>
                      <TableCell>
                        {currentView === 'member-completions-table' ? 'Completion Date' : 'Due Date'}
                      </TableCell>
                      <TableCell>Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const data = 
                        currentView === 'member-enrollments-table' ? memberData.enrollments : 
                        currentView === 'member-views-table' ? memberData.views : 
                        memberData.completions;
                      
                      if (data && data.length > 0) {
                        return data.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.learning_name}</TableCell>
                            <TableCell>{formatDate(item.enrollment_date)}</TableCell>
                            <TableCell>
                              {currentView === 'member-completions-table' 
                                ? formatDate(item.completion_date) 
                                : formatDate(item.due_date)}
                            </TableCell>
                            <TableCell>{item.progress || 0}%</TableCell>
                          </TableRow>
                        ));
                      } else {
                        const dataType = 
                          currentView === 'member-enrollments-table' ? 'enrollments' : 
                          currentView === 'member-views-table' ? 'views' : 'completions';
                        return (
                          <TableRow>
                            <TableCell colSpan={4} align="center">No {dataType} data found</TableCell>
                          </TableRow>
                        );
                      }
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
        </div>
      )}

      {/* Learnings Tab Content */}
      {activeTab === 1 && (
        <div>
          {currentView === 'learnings-main-table' && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Learnings ({learningsData.length})</TableCell>
                    <TableCell>Enrollments</TableCell>
                    <TableCell>In Progress</TableCell>
                    <TableCell>Completions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {learningsData && learningsData.length > 0 ? (
                    learningsData.map((learning) => (
                      <TableRow key={learning.id}>
                        <TableCell>{learning.name}</TableCell>
                        <TableCell>
                          <Link 
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              fetchLearningPeopleData('enrollments', learning.id, learning.type, learning.name);
                            }}
                          >
                            {learning.enrollments || 0}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link 
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              fetchLearningPeopleData('views', learning.id, learning.type, learning.name);
                            }}
                          >
                            {learning.views || 0}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link 
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              fetchLearningPeopleData('completions', learning.id, learning.type, learning.name);
                            }}
                          >
                            {learning.completions || 0}
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No learning data found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {currentView === 'learning-people-table' && (
            <div>
              <Box sx={{ mb: 2 }}>
                <Link 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentView('learnings-main-table');
                  }}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <ArrowBack fontSize="small" sx={{ mr: 1 }} />
                  Back to Learnings
                </Link>
              </Box>
              <Typography variant="h6" gutterBottom>
                {selectedLearning.dataType === 'enrollments' ? 'enrolled in' : 
                 selectedLearning.dataType === 'views' ? 'progressed in' : 'completed in'} {selectedLearning.name}
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>People ({learningPeopleData.length})</TableCell>
                      <TableCell>Enrollment Date</TableCell>
                      <TableCell>
                        {selectedLearning.dataType === 'completions' ? 'Completion Date' : 'Due Date'}
                      </TableCell>
                      <TableCell>Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {learningPeopleData && learningPeopleData.length > 0 ? (
                      learningPeopleData.map((person, index) => (
                        <TableRow key={index}>
                          <TableCell>{person.name}</TableCell>
                          <TableCell>{formatDate(person.enrollment_date)}</TableCell>
                          <TableCell>
                            {selectedLearning.dataType === 'completions' 
                              ? formatDate(person.completion_date) 
                              : formatDate(person.due_date)}
                          </TableCell>
                          <TableCell>{person.progress || 0}%</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No people found for this {selectedLearning.dataType} data
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
        </div>
      )}
    </Container>
  );
};

export default LearningSum;
